import { BrowserRouter, Routes, Route, useLocation, Navigate, Outlet } from "react-router-dom";
import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { FavoritesProvider } from "@/context/FavoritesContext";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import EmailVerifyBanner from "@/components/EmailVerifyBanner";
import Landing from "@/pages/Landing";
import Browse from "@/pages/Browse";
import VendorProfile from "@/pages/VendorProfile";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";
import PlannerDashboard from "@/pages/PlannerDashboard";
import VendorDashboard from "@/pages/VendorDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import EventDetail from "@/pages/EventDetail";
import BookingDetail from "@/pages/BookingDetail";
import DesignPreview from "@/pages/DesignPreview";
import TermsPlanner from "@/pages/TermsPlanner";
import TermsVendor from "@/pages/TermsVendor";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import PaymentPolicy from "@/pages/PaymentPolicy";
import OnboardingWizard from "@/pages/OnboardingWizard";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import Install from "@/pages/Install";
import VerifyEmail from "@/pages/VerifyEmail";
import { ForgotPassword, ResetPassword } from "@/pages/PasswordReset";
import LegalReacceptanceModal from "@/components/LegalReacceptanceModal";
import InstallPrompt from "@/components/InstallPrompt";
import { InstallProvider, useInstall } from "@/context/InstallContext";
import { Toaster } from "@/components/ui/sonner";

const HEADERLESS_ROUTES = new Set(["/login", "/register"]);
const FOOTERLESS_ROUTES = new Set(["/login", "/register", "/forgot-password", "/reset-password", "/verify-email"]);

function Layout() {
  const { pathname } = useLocation();
  const showHeader = !HEADERLESS_ROUTES.has(pathname);
  const showFooter = !FOOTERLESS_ROUTES.has(pathname);
  return (
    <div className="app-shell">
      {showHeader && <Header />}
      <EmailVerifyBanner />
      <main className="min-h-[70vh] app-shell-main"><Outlet /></main>
      {showFooter && <Footer />}
    </div>
  );
}

function Protected({ roles, children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-[60vh] flex items-center justify-center text-[#5C5C5C]">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

// Gate that forces uninstalled/unauthenticated consumers to /install before any
// vendor-marketplace component (which triggers data fetches) mounts.
// Authentication is authoritative: signed-out users are always redirected
// (to /login if they've installed the PWA, to /install otherwise).
// Additionally: vendor accounts have no business browsing other vendors, so
// they are redirected straight to their own dashboard.
function ConsumerGate({ children }) {
  const { isStandalone } = useInstall();
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-[60vh] flex items-center justify-center text-[#5C5C5C]">Loading…</div>;
  if (user?.role === "vendor") return <Navigate to="/vendor/dashboard" replace />;
  if (user) return children;
  if (isStandalone) return <Navigate to="/login" replace />;
  return <Navigate to="/install" replace />;
}

// Same as ConsumerGate but for the vendor-detail route. A vendor may still
// view their OWN vendor profile (to preview how planners see it); every other
// vendor listing is off-limits and redirects to their dashboard. Ownership is
// re-checked authoritatively inside VendorProfile using the vendor doc.
function VendorProfileGate({ children }) {
  const { isStandalone } = useInstall();
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-[60vh] flex items-center justify-center text-[#5C5C5C]">Loading…</div>;
  if (user?.role === "vendor") {
    // Vendor: allow the page to render — the page itself enforces the
    // owner-only redirect once the vendor doc is loaded.
    return children;
  }
  if (user) return children;
  if (isStandalone) return <Navigate to="/login" replace />;
  return <Navigate to="/install" replace />;
}

function AppRouter() {
  const location = useLocation();
  // Emergent Google Auth callback detection
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route element={<Layout />}>
        <Route path="/" element={<ConsumerGate><Landing /></ConsumerGate>} />
        <Route path="/browse" element={<ConsumerGate><Browse /></ConsumerGate>} />
        <Route path="/vendor/:id" element={<VendorProfileGate><VendorProfile /></VendorProfileGate>} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/planner/dashboard" element={<Protected roles={["planner", "admin"]}><PlannerDashboard /></Protected>} />
        <Route path="/vendor/dashboard" element={<Protected roles={["vendor", "admin"]}><VendorDashboard /></Protected>} />
        <Route path="/admin" element={<Protected roles={["admin"]}><AdminDashboard /></Protected>} />
        <Route path="/events/:id" element={<Protected><EventDetail /></Protected>} />
        <Route path="/bookings/:id" element={<Protected><BookingDetail /></Protected>} />
        <Route path="/design-preview" element={<DesignPreview />} />
        <Route path="/terms/planner" element={<TermsPlanner />} />
        <Route path="/terms/vendor" element={<TermsVendor />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/payment-policy" element={<PaymentPolicy />} />
        <Route path="/onboarding" element={<Protected roles={["vendor", "admin"]}><OnboardingWizard /></Protected>} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
        <Route path="/install" element={<Install />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <FavoritesProvider>
          <InstallProvider>
            <BrowserRouter>
              <AppRouter />
              <LegalReacceptanceModal />
              <InstallPrompt />
              <Toaster position="bottom-right" richColors />
            </BrowserRouter>
          </InstallProvider>
        </FavoritesProvider>
      </AuthProvider>
    </div>
  );
}
